import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Info, Copy } from 'lucide-react';
import { useCloneVolume, useListSnapshots, useListStorageClasses } from '@/service/gpu-instance/volume-hook';
import type { Volume } from '@/service/gpu-instance/volume-type';
import { SnapshotStatus } from '@/service/gpu-instance/volume-type';
import { toast } from 'sonner';

interface CloneVolumeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  volume: Volume | null;
  onSuccess?: () => void;
}

export const CloneVolumeDialog = ({
  open,
  onOpenChange,
  volume,
  onSuccess,
}: CloneVolumeDialogProps) => {
  const [name, setName] = useState('');
  const [snapshotId, setSnapshotId] = useState<string>('');
  const [storageClassId, setStorageClassId] = useState<string>('');

  const cloneVolume = useCloneVolume();
  const { data: snapshots = [] } = useListSnapshots(volume?.id);
  const { data: storageClasses = [] } = useListStorageClasses();

  // Filter ready snapshots
  const availableSnapshots = snapshots.filter((snap) => snap.status === SnapshotStatus.Ready);

  if (!volume) return null;

  const validateName = (value: string) => {
    return /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(value);
  };

  const isValid = name.length >= 1 && validateName(name);

  const selectedStorageClass = storageClassId
    ? storageClasses.find((sc) => sc.id === storageClassId)
    : volume.storageClass;

  const estimatedCost = selectedStorageClass
    ? (volume.requestedSize * selectedStorageClass.pricePerGBMonth).toFixed(2)
    : '0.00';

  const handleClone = async () => {
    if (!isValid) return;

    try {
      await cloneVolume.mutateAsync({
        sourceVolumeId: volume.id,
        name,
        snapshotId: snapshotId || undefined,
        storageClassId: storageClassId || undefined,
      });
      toast.success('Volume clone initiated', {
        description: `Cloning "${volume.name}" to "${name}".`,
      });
      onOpenChange(false);
      onSuccess?.();
      // Reset form
      setName('');
      setSnapshotId('');
      setStorageClassId('');
    } catch {
      toast.error('Failed to clone volume');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Clone Volume
          </DialogTitle>
          <DialogDescription>
            Create a copy of "{volume.name}" with all its data.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              The clone will be an independent copy of the source volume.
              Changes to one will not affect the other.
            </AlertDescription>
          </Alert>

          {/* Source Volume Info */}
          <div className="rounded-lg bg-muted/50 p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Source Volume</span>
              <span className="font-medium">{volume.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Size</span>
              <span>{volume.requestedSize} GB</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Used</span>
              <span>{volume.usedSize} GB ({Math.round((volume.usedSize / volume.requestedSize) * 100)}%)</span>
            </div>
          </div>

          {/* New Volume Name */}
          <div className="space-y-2">
            <Label htmlFor="clone-name">New Volume Name</Label>
            <Input
              id="clone-name"
              placeholder={`${volume.name}-clone`}
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase())}
              className={name && !validateName(name) ? 'border-destructive' : ''}
            />
            <p className="text-xs text-muted-foreground">
              Lowercase letters, numbers, and dashes only.
            </p>
          </div>

          {/* Clone from Snapshot (optional) */}
          {availableSnapshots.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="snapshot">Clone from Snapshot (optional)</Label>
              <Select value={snapshotId} onValueChange={setSnapshotId}>
                <SelectTrigger>
                  <SelectValue placeholder="Use current volume state" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Use current volume state</SelectItem>
                  {availableSnapshots.map((snap) => (
                    <SelectItem key={snap.id} value={snap.id}>
                      <div className="flex flex-col">
                        <span>{snap.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(snap.createdAt).toLocaleString()} - {snap.size} GB
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Optionally clone from a point-in-time snapshot instead of current state.
              </p>
            </div>
          )}

          {/* Storage Class (optional) */}
          <div className="space-y-2">
            <Label htmlFor="storage-class">Storage Class (optional)</Label>
            <Select value={storageClassId} onValueChange={setStorageClassId}>
              <SelectTrigger>
                <SelectValue placeholder={`Same as source (${volume.storageClass.displayName})`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Same as source ({volume.storageClass.displayName})</SelectItem>
                {storageClasses
                  .filter((sc) => sc.id !== volume.storageClass.id)
                  .map((sc) => (
                    <SelectItem key={sc.id} value={sc.id}>
                      {sc.displayName} - ${sc.pricePerGBMonth}/GB/mo
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Optionally use a different storage class for the clone.
            </p>
          </div>

          {/* Cost Estimate */}
          <div className="rounded-lg bg-muted/50 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Estimated Monthly Cost</span>
              <span className="text-lg font-bold">${estimatedCost}/mo</span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {volume.requestedSize} GB x ${selectedStorageClass?.pricePerGBMonth}/GB
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleClone} disabled={!isValid || cloneVolume.isPending}>
            {cloneVolume.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Clone Volume
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CloneVolumeDialog;
